import { Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { BiReport } from '@app/core/model/entities/bi-report/bi-report';
import { PowerBiReportConfig } from '@app/core/model/other/power-bi';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { PowerBiService } from '@services/power-bi.service';
import { IReportEmbedConfiguration } from 'embed';
import { filter, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'power-bi-report',
  templateUrl: './power-bi-report.component.html',
  styleUrls: ['./power-bi-report.component.scss'],
  standalone: false
})
export class PowerBiReportComponent implements OnInit {

  @Input() public currentReport: FormControl<BiReport>;
  public currentReportId: string;
  public workspaceId: string;
  @Input() public entityId?: string;
  @Input() public filterTable?: string;
  @ViewChild('powerBi') public powerBi: ElementRef;
  @Input() public relatedEntityType: EntityTypeEnum;
  public powerBiReportConfig: PowerBiReportConfig & IReportEmbedConfiguration;
  public exportInProgress = false;
  public Permission = PermissionEnum;

  private readonly powerBiService = inject(PowerBiService);
  private readonly snackbar = inject(SnackbarManager);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly accessManager = inject(AccessManager);
  private readonly appManager = inject(AppManager);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Load current Bi report config to display.
   */
  public ngOnInit() {
    this.workspaceId = this.appManager.currentOrganization?.properties['workspaceId']
      ?? this.accessManager.currentUser.workspaceId;
    this.currentReport.valueChanges
      .pipe(
        startWith(this.currentReport.value),
        filter(report => !!report),
        tap(report => {
          this.powerBiReportConfig = null;
          this.currentReportId = report.reportId;
        }),
        switchMap(report =>
          this.powerBiService.loadPowerBiReportConfig(this.workspaceId, report.reportId)
            .pipe(
              tap(config => {
                this.powerBiReportConfig = this.addFiltersToBiReportConfig(config);
                this.powerBiReportConfig.pageName = report.properties['reportMainPage'];
                this.analyticsService.trackEvent(new AnalyticsEvent(ActionEnum.READ, this.relatedEntityType)
                  .addProperties({
                    [AnalyticsKeyEnum.REPORT_TYPE]: EntityTypeEnum.BI_REPORT,
                    [AnalyticsKeyEnum.REPORT_ID]: report.reportId
                  }));
              })
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Add user and entity filters to a Power BI report config.
   * @param config PowerBiReportConfig to update by adding filters.
   * @return PowerBiReportConfig Config with user and entity filters.
   */
  private addFiltersToBiReportConfig(config: PowerBiReportConfig): PowerBiReportConfig {
    // Add filter by Users
    config.filters.push(
      PowerBiReportConfig.buildFilter(
        'user',
        'id',
        this.accessManager.currentUser.id
      )
    );

    // Add filter by entity if needed
    if (!!this.entityId && !!this.filterTable) {
      config.filters.push(PowerBiReportConfig.buildFilter(this.filterTable, '_id', this.entityId));
    }

    return config;
  }

  /**
   * Call the service to print the Power BI report in pdf.
   */
  public exportBiReport(): void {
    this.powerBiService.exportPowerBiReports(
      this.workspaceId,
      this.currentReportId,
      this.powerBiReportConfig.filters
    )
      .pipe(
        tap(() => {
          this.analyticsService.trackEvent(
            new AnalyticsEvent(ActionEnum.EXPORT, this.relatedEntityType)
              .addProperties({
                [AnalyticsKeyEnum.REPORT_TYPE]: EntityTypeEnum.BI_REPORT,
                [AnalyticsKeyEnum.REPORT_ID]: this.currentReportId
              })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() =>
        this.snackbar.showActionSnackbar(this.translate.instant('SUCCESS.SEND_EMAIL_EXPORT_REPORTS'))
      );
  }

  /**
   * Toggle full screen mode for the Power BI report
   */
  public toggleFullScreen(): void {
    !document.fullscreenElement ? this.powerBi.nativeElement.requestFullscreen() : document.exitFullscreen();
  }

  /**
   * Print the PowerBI report
   */
  public print(): void {
    const container = this.powerBi?.nativeElement?.querySelector('.report-container') as HTMLElement | null;
    if (container) {
      (window as any).powerbi.get(container).print();
    }
  }
}
